import React from "react";
import { MemoryRouter } from "react-router-dom";
//Material imports
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
//Limitless imports
import { ClientContext, log } from "@limitless/common/utils";
import { useTranslation, useResources } from "@limitless/common/hooks";
import { shouldBlockAppEntry } from "@limitless/customer/utils";
import { useCustomerContext, useCustomerMessageContext } from "@limitless/customer/hooks";
import { createTheme } from "@limitless/customer/theme/Theme";
import Routes from "./routes/Routes";
import Fab from "./components/fab/Fab";
import ReachOutExperience from "./components/reach-out-experience/ReachOutExperience";
import { useAppVisibilityContext } from "./hooks/app-visibility/AppVisibilityContext";
import { useMobileDetectionContext } from "./hooks/mobile-detection/MobileDetectionContext";
import { useReachOutContext } from "./hooks/reach-out/ReachOutContext";
import useSessionMessage from "./hooks/session-message/SessionMessageHook";
import { log as pilog } from "@limitless/common/utils";

const LiveMessenger = () => {
    const { appVisibility, toggleAppOpen, unReadMessage, isFabVisible } = useAppVisibilityContext();
    const { isMobileDevice, isFillAvailableSpace, toggleFileSpace } = useMobileDetectionContext();
    const {
        customer: { emailViewed }
    } = useCustomerContext();
    const { message } = useCustomerMessageContext();
    const {
        resources: { upgradeContent, uilabels, theme, loading, llmConfig }
    } = useResources();
    const { setResourceBundle } = useTranslation();
    const { isReachOutMode } = useReachOutContext();
    const { sessionLastMessage, sessionLoading } = useSessionMessage();
    const log = pilog.getLogger("LiveMessenger events");

    const [llmState, setllmState] = React.useState({
        fromEmail: false,
        sessionMessage: null
    });

    const { isAppVisible, isAppOpen } = appVisibility;

    React.useEffect(() => {
        if (sessionLastMessage && !fromEmail) {
            setllmState({ ...llmState, sessionMessage: sessionLastMessage });
            if (typeof ClientContext._appVisible !== "string" && ClientContext._fabButtonStyle !== "hidden") {
                handleFABClick();
            }
        }
    }, [sessionLastMessage]);

    React.useEffect(() => {
        if (llmConfig) {
            ClientContext.validateConfig("limitless-root", llmConfig.attribute);
        }
    }, [llmConfig]);

    React.useEffect(() => {
        if (!loading && isAppVisible && !sessionLoading ) {
            log.info("LiveMessenger:","Render successfully");
        }
    }, [loading , isAppVisible, sessionLoading ]);

    React.useEffect(() => {
        toggleFileSpace(ClientContext._fillAvailableSpace);
    }, [ClientContext?._fillAvailableSpace]);

    React.useEffect(() => {
        const newState = {};
        //check the window query parameters to see if this came from an email
        const fromEmail = ClientContext._customerUtsc
            ? ClientContext.validateQueryParameters("messageId")
            : ClientContext.validateQueryParameters("messageId", "accessKey");
        //force the LLM window if we've come from an email
        if (fromEmail) {
            newState.fromEmail = true;
            toggleAppOpen();
        }
        log.debug("New State ", newState);
        setllmState({ ...llmState, ...newState });
    }, []);

    React.useEffect(() => {
        // check to see if we have a session message
        if (message.accessKey) {
            const sessionMessage = {
                messageId: message.messageId,
                accessKey: message.accessKey
            };
            setllmState({ ...llmState, sessionMessage });
        }
    }, [isAppOpen]);

    React.useEffect(() => {
        if (!loading) {
            setResourceBundle(uilabels);
        }
    }, [loading]);

    React.useEffect(() => {
        if (emailViewed && llmState.fromEmail) {
            setllmState({ ...llmState, fromEmail: false });
        }
    }, [emailViewed]);

    const handleFABClick = () => {
        toggleAppOpen();
    };

    const anchorStyles = React.useMemo(() => {
        const styles = {};
        //llm-anchor provides styles sticky position for smooth mobile scrolling of Fab, this needs to be changed to fixed when LLM launches
        if (isAppVisible) {
            styles.position = "fixed";
        }

        if (isAppOpen && isMobileDevice) {
            styles.width = "100vw";
            styles.height = "100%";
        }

        return styles;
    }, [isAppOpen, isAppVisible, isMobileDevice]);

    const maintenanceContent = React.useMemo(() => shouldBlockAppEntry("web", upgradeContent), [upgradeContent]);

    const memoTheme = React.useMemo(() => createTheme(theme), [theme]);

    if (maintenanceContent.osContent && maintenanceContent.osContent.hideLLM === true) {
        //eslint-disable-next-line
        return <div data-llm-maintenance={true} />;
    }

    const { fromEmail, sessionMessage } = llmState;

    return (
        <div>
            {!loading && isAppVisible && !sessionLoading && (
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={memoTheme}>
                        <div className="llm-anchor" style={anchorStyles}>
                            {isAppOpen && (
                                <div className={`llm-shadow ${isFillAvailableSpace ? "llm-fill-available-space" : ""}`}>
                                    {/* MemoryRouter lets us have navigation without updating the address bar */}
                                    <MemoryRouter>
                                        <Routes
                                            fromEmail={fromEmail}
                                            sessionMessage={sessionMessage}
                                            maintenanceContent={maintenanceContent}
                                        />
                                    </MemoryRouter>
                                </div>
                            )}
                            {isFabVisible && (
                                <Fab
                                    isLLMVisible={isAppOpen}
                                    isMobileDevice={isMobileDevice}
                                    onClick={handleFABClick}
                                    unReadMessage={unReadMessage && !isAppOpen}
                                />
                            )}
                        </div>
                    </ThemeProvider>
                </StyledEngineProvider>
            )}
            {!loading && isReachOutMode && <ReachOutExperience onFABClick={handleFABClick} />}
        </div>
    );
};

export default LiveMessenger;
